import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";
import Spinner from "../components/Spinner";
import StrategyAdherenceCard from "../components/StrategyAdherenceCard";
import AutopilotPanel from "../components/AutopilotPanel";
import AutopilotStatusBlock from "../components/AutopilotStatusBlock";
import {AUTOPILOT_LABELS, AUTOPILOT_LEVELS, archetypeLabel, autopilotLabel} from "../support/strategyLabels";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface StrategyArchetype {
    code: string;
    defaultTargetSize: number | null;
    defaultDeadStockThresholdDays: number | null;
    defaultAutopilotLevel: string | null;
}

interface Strategy {
    configured: boolean;
    archetype: string | null;
    targetSize: number | null;
    deadStockThresholdDays: number | null;
    monthlyBudget: number | null;
    styleTarget: string | null;
    autopilotLevel: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

const StrategyPage: React.FC = () => {
    const {selectedOwnerId, isOwnWardrobe} = useWardrobe();

    const [archetypes, setArchetypes] = useState<StrategyArchetype[]>([]);
    const [strategy, setStrategy] = useState<Strategy | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // Form state
    const [archetype, setArchetype] = useState<string>('');
    const [targetSize, setTargetSize] = useState<string>('');
    const [deadStockDays, setDeadStockDays] = useState<string>('');
    const [monthlyBudget, setMonthlyBudget] = useState<string>('');
    const [styleTarget, setStyleTarget] = useState<string>('');
    const [autopilotLevel, setAutopilotLevel] = useState<string>('ASSIST');

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const [archRes, stratRes] = await Promise.all([
                api.get<StrategyArchetype[]>('/strategy/archetypes'),
                api.get<Strategy>('/strategy', {params}),
            ]);
            setArchetypes(archRes.data);
            const s = stratRes.data;
            setStrategy(s);
            if (s.configured) {
                setArchetype(s.archetype || '');
                setTargetSize(s.targetSize != null ? String(s.targetSize) : '');
                setDeadStockDays(s.deadStockThresholdDays != null ? String(s.deadStockThresholdDays) : '');
                setMonthlyBudget(s.monthlyBudget != null ? String(s.monthlyBudget) : '');
                setStyleTarget(s.styleTarget || '');
                setAutopilotLevel(s.autopilotLevel || 'ASSIST');
            }
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    useDocumentTitle();

    useEffect(() => {
        load();
    }, [load]);

    const onSelectArchetype = (code: string) => {
        setArchetype(code);
        setSaved(false);
        const preset = archetypes.find(a => a.code === code);
        if (preset) {
            setTargetSize(preset.defaultTargetSize != null ? String(preset.defaultTargetSize) : '');
            setDeadStockDays(preset.defaultDeadStockThresholdDays != null ? String(preset.defaultDeadStockThresholdDays) : '');
            if (preset.defaultAutopilotLevel) {
                setAutopilotLevel(preset.defaultAutopilotLevel);
            }
        }
    };

    const onSave = async () => {
        if (!archetype) {
            return;
        }
        setSaving(true);
        setSaved(false);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const payload = {
                archetype,
                targetSize: targetSize ? Number(targetSize) : null,
                deadStockThresholdDays: deadStockDays ? Number(deadStockDays) : null,
                monthlyBudget: monthlyBudget ? Number(monthlyBudget) : null,
                styleTarget: styleTarget.trim() || null,
                autopilotLevel,
            };
            const res = await api.put<Strategy>('/strategy', payload);
            setStrategy(res.data);
            setSaved(true);
        } catch (e) {
            setError(true);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Spinner/>;
    }

    if (!isOwnWardrobe) {
        return (
            <div className="p-4 md:p-6 space-y-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Стратегия гардероба</h1>
                {strategy?.configured ? (
                    <>
                        <StrategyAdherenceCard ownerId={selectedOwnerId}/>
                        <AutopilotStatusBlock ownerId={selectedOwnerId} canEdit={false}/>
                        <AutopilotPanel ownerId={selectedOwnerId} canRun={false}/>
                        <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-2"
                             data-testid="strategy-readonly">
                            <p className="text-gray-900 dark:text-gray-100">
                                Архетип: <span className="font-semibold">
                                {archetypeLabel(strategy.archetype)}</span>
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                Автономия: {autopilotLabel(strategy.autopilotLevel)}
                            </p>
                        </div>
                    </>
                ) : (
                    <p className="text-gray-500 dark:text-gray-400" data-testid="strategy-readonly-empty">
                        Владелец ещё не выбрал стратегию для этого гардероба.
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-2xl">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Стратегия гардероба</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                    Стратегия задаёт правила, по которым система ведёт ваш гардероб: что хранить,
                    что предлагать к выбытию и насколько самостоятельно действовать.
                </p>
            </div>

            {error && (
                <p className="text-rose-600 dark:text-rose-400" data-testid="strategy-error">
                    Не удалось сохранить стратегию. Попробуйте ещё раз.
                </p>
            )}

            {strategy?.configured && <StrategyAdherenceCard ownerId={selectedOwnerId}/>}
            {/* Один статус-блок автопилота на экран: расписание, режим и фоновая задача вместе. */}
            {strategy?.configured && <AutopilotStatusBlock ownerId={selectedOwnerId} canEdit={isOwnWardrobe}/>}
            {strategy?.configured && <AutopilotPanel ownerId={selectedOwnerId} canRun={isOwnWardrobe}/>}

            <section>
                <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Архетип</h2>
                <div className="flex flex-wrap gap-2" data-testid="archetype-options">
                    {archetypes.map(a => (
                        <button
                            key={a.code}
                            type="button"
                            onClick={() => onSelectArchetype(a.code)}
                            aria-pressed={archetype === a.code}
                            className={`min-h-[44px] px-4 py-2 rounded-xl border text-sm transition-colors ${
                                archetype === a.code
                                    ? 'bg-brand-600 text-white border-brand-600'
                                    : 'bg-white dark:bg-ink-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-ink-700 hover:border-brand-500'
                            }`}
                        >
                            {archetypeLabel(a.code)}
                        </button>
                    ))}
                </div>
            </section>

            <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Целевой размер гардероба</span>
                    <input
                        type="number" min={1} inputMode="numeric"
                        value={targetSize}
                        onChange={e => {
                            setTargetSize(e.target.value);
                            setSaved(false);
                        }}
                        className="min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                        aria-label="Целевой размер гардероба"
                    />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Порог «давно не носили», дней</span>
                    <input
                        type="number" min={1} inputMode="numeric"
                        value={deadStockDays}
                        onChange={e => {
                            setDeadStockDays(e.target.value);
                            setSaved(false);
                        }}
                        className="min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                        aria-label="Порог dead-stock в днях"
                    />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Месячный бюджет</span>
                    <input
                        type="number" min={0} inputMode="numeric"
                        value={monthlyBudget}
                        onChange={e => {
                            setMonthlyBudget(e.target.value);
                            setSaved(false);
                        }}
                        className="min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                        aria-label="Месячный бюджет"
                    />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Стилевая цель</span>
                    <input
                        type="text" maxLength={255}
                        value={styleTarget}
                        onChange={e => {
                            setStyleTarget(e.target.value);
                            setSaved(false);
                        }}
                        className="min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                        aria-label="Стилевая цель"
                    />
                </label>
            </section>

            <section>
                <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Уровень автономии</h2>
                <div className="flex flex-col gap-2" role="radiogroup" aria-label="Уровень автономии">
                    {AUTOPILOT_LEVELS.map(level => (
                        <label key={level} className="flex items-center gap-3 min-h-[44px] cursor-pointer">
                            <input
                                type="radio"
                                name="autopilot"
                                value={level}
                                checked={autopilotLevel === level}
                                onChange={() => {
                                    setAutopilotLevel(level);
                                    setSaved(false);
                                }}
                                className="accent-brand-600"
                            />
                            <span className="text-gray-900 dark:text-gray-100">{AUTOPILOT_LABELS[level]}</span>
                        </label>
                    ))}
                </div>
            </section>

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onSave}
                    data-testid="strategy-save"
                    disabled={!archetype || saving}
                    className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                >
                    {saving ? 'Сохранение…' : 'Сохранить стратегию'}
                </button>
                {saved && (
                    <span className="text-emerald-600 dark:text-emerald-400" data-testid="strategy-saved">
                        Сохранено
                    </span>
                )}
            </div>
        </div>
    );
};

export default StrategyPage;
